import { MonetaryAmount } from '@components'
import cn from 'classnames'
import { motion } from 'framer-motion'
import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

interface Props {
  to: string
  icon: React.ReactNode
  iconColorHex: string
  isFirst: boolean
  isLast: boolean
  isSelected?: boolean

  // Fields
  date: string | React.ReactNode
  contact: string
  amount: number
  statusDot: React.ReactNode
}

export const GeneralListItem: React.FC<Props> = ({
  to,
  icon,
  iconColorHex,
  isFirst,
  isLast,
  isSelected,
  date,
  contact,
  amount,
  statusDot
}) => (
  <StyledListItem
    to={to}
    className={cn({ isFirst, isLast, isSelected })}
    data-test="invoice-list-item"
  >
    <StyledListItemIcon iconColor={iconColorHex}>{icon}</StyledListItemIcon>
    <StyledListItemRows>
      <StyledListItemRow>
        <InvoiceDate>
          <span>{date}</span>
        </InvoiceDate>
        <InvoiceDate>{statusDot}</InvoiceDate>
      </StyledListItemRow>
      <StyledListItemRow>
        <InvoiceContactName>{contact}</InvoiceContactName>
        <InvoiceAmount>
          <MonetaryAmount showColor value={amount} />
        </InvoiceAmount>
      </StyledListItemRow>
    </StyledListItemRows>
  </StyledListItem>
)

const StyledListItem = styled(Link)`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
  background: ${({ theme }) => theme.colors.neutralWhite};
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 0.5px solid #f6f7f9;

  &.isFirst {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &.isLast {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}rem;
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}rem;
  }

  &:hover,
  &.isSelected {
    text-decoration: none;
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const StyledListItemIcon = styled(motion.div).attrs({
  whileHover: {
    scale: 1.1
  }
})<{ iconColor: string }>`
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  min-width: 2.4rem;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ iconColor }) => iconColor}22;

  svg {
    fill: ${({ iconColor }) => iconColor};
    width: 1rem;
    height: 1rem;
  }
`

const StyledListItemRows = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const StyledListItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  min-height: 1.0625rem;

  &:not(:last-of-type) {
    margin-bottom: ${({ theme }) => theme.spacing.xxs}rem;
  }
`

const InvoiceDate = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  min-width: 0;
  overflow: hidden;

  &:last-child {
    flex-shrink: 0;
    overflow: visible;
  }
`

const InvoiceContactName = styled.span`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: 0.2s;
`

const InvoiceAmount = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  white-space: nowrap;
`
